import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  listAgencies,
  createAgency,
  updateAgency,
  deleteAgency,
} from "@/lib/api/agencies";
import { listGroups } from "@/lib/api/groups";
import { getErrorMessage } from "@/lib/api/client";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type { AgencyWithGroup } from "@/types";

export const Route = createFileRoute("/_dashboard/admin/agencies")({
  component: AgenciesPage,
});

const agencySchema = z.object({
  name: z.string().min(1, "Le nom est requis"),
  group_id: z.number({ required_error: "Le groupe est requis" }).min(1, "Le groupe est requis"),
});

type AgencyForm = z.infer<typeof agencySchema>;

function AgenciesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filterGroupId, setFilterGroupId] = useState<number | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<AgencyWithGroup | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["agencies", search, filterGroupId],
    queryFn: () => listAgencies(filterGroupId, search || undefined),
  });

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => listGroups(),
  });

  const groups = groupsData?.items ?? [];

  const createForm = useForm<AgencyForm>({
    resolver: zodResolver(agencySchema),
    defaultValues: { name: "", group_id: 0 },
  });

  const editForm = useForm<AgencyForm>({
    resolver: zodResolver(agencySchema),
  });

  const createMutation = useMutation({
    mutationFn: createAgency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      toast.success("Agence créée");
      setCreateOpen(false);
      createForm.reset({ name: "", group_id: 0 });
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AgencyForm }) =>
      updateAgency(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      toast.success("Agence mise à jour");
      setEditOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAgency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agencies"] });
      toast.success("Agence supprimée");
      setDeleteOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const handleEdit = (agency: AgencyWithGroup) => {
    setSelected(agency);
    editForm.reset({ name: agency.name, group_id: agency.group_id });
    setEditOpen(true);
  };

  const handleDelete = (agency: AgencyWithGroup) => {
    setSelected(agency);
    setDeleteOpen(true);
  };

  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <Header title="Agences" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-1 gap-3 items-center">
              <div className="relative flex-1 sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher une agence..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={filterGroupId !== undefined ? String(filterGroupId) : "all"}
                onValueChange={(v) =>
                  setFilterGroupId(v === "all" ? undefined : Number(v))
                }
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Tous les groupes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les groupes</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle agence
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Groupe</TableHead>
                    <TableHead>Créé le</TableHead>
                    <TableHead className="w-[120px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                      </TableRow>
                    ))
                  ) : data?.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="h-32 text-center">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <Building2 className="h-10 w-10 opacity-50" />
                          <p>Aucune agence</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data?.items.map((agency) => (
                      <TableRow key={agency.id}>
                        <TableCell className="font-medium">{agency.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{agency.group_name || "-"}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {new Date(agency.created_at).toLocaleDateString("fr-FR")}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(agency)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(agency)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {data && (
            <p className="text-sm text-muted-foreground">
              {data.total_items} agence{data.total_items > 1 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvelle agence</DialogTitle>
              <DialogDescription>
                Créez une agence rattachée à un groupe (région)
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={createForm.handleSubmit((d) => createMutation.mutate(d))}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input
                  {...createForm.register("name")}
                  placeholder="Ex : LYON, MARSEILLE, BREST..."
                />
                {createForm.formState.errors.name && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Groupe (Région)</Label>
                <Select
                  value={
                    createForm.watch("group_id")
                      ? String(createForm.watch("group_id"))
                      : ""
                  }
                  onValueChange={(v) =>
                    createForm.setValue("group_id", Number(v), {
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un groupe" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {createForm.formState.errors.group_id && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.group_id.message}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setCreateOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Création..." : "Créer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier l&apos;agence</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={editForm.handleSubmit(
                (d) => selected && updateMutation.mutate({ id: selected.id, data: d })
              )}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input {...editForm.register("name")} />
                {editForm.formState.errors.name && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Groupe (Région)</Label>
                <Select
                  value={
                    editForm.watch("group_id")
                      ? String(editForm.watch("group_id"))
                      : ""
                  }
                  onValueChange={(v) =>
                    editForm.setValue("group_id", Number(v), {
                      shouldValidate: true,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un groupe" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editForm.formState.errors.group_id && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.group_id.message}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setEditOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer l&apos;agence</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer l&apos;agence &quot;{selected?.name}&quot; ?
                L&apos;agence ne peut être supprimée que si elle n&apos;est liée à aucune cotation.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => selected && deleteMutation.mutate(selected.id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminGuard>
  );
}
